'use client';

import Image from 'next/image';
import Link from 'next/link';
import { FiArrowRight, FiPlay } from 'react-icons/fi';
import { useRouter } from 'next/router';
import { useCart } from '../../lib/CartContext';
import { useLocalePath } from '../../lib/useLocalePath';

export type FeaturedProduct = {
  id: string;
  title: string;
  slug?: string;
  imageUrl?: string;
  price: number;
  categoryName?: string;
  discountPercent?: number | null;
  variantId?: string;
  variantLabel?: string;
};

type FeaturedProductSpotlightProps = {
  product: FeaturedProduct;
  productHref: string;
};

const formatPrice = (value: number) => `€${Number(value || 0).toFixed(2)}`;

export default function FeaturedProductSpotlight({ product, productHref }: FeaturedProductSpotlightProps) {
  const router = useRouter();
  const { addProduct } = useCart();
  const { prefix } = useLocalePath();
  const hasDiscount = Number(product.discountPercent || 0) > 0;
  const finalPrice = hasDiscount
    ? product.price * (1 - Number(product.discountPercent || 0) / 100)
    : product.price;

  const handleBuyNow = () => {
    addProduct({
      id: product.id,
      title: product.title,
      imageUrl: product.imageUrl || '/no-image.png',
      price: finalPrice.toFixed(2),
      mode: 'payment',
      stripePriceId: product.id,
      ...(product.variantId ? { variantId: product.variantId } : {}),
      ...(product.variantLabel ? { variantLabel: product.variantLabel } : {}),
    });

    router.push(`${prefix}/cart`);
  };

  return (
    <section className="w-full bg-[#e8e8e8] dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-14">
        <div className="rounded-2xl border border-gray-200 dark:border-[rgba(48,54,61,0.6)] bg-white dark:bg-[rgba(13,17,23,0.65)] p-5 sm:p-7 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-md text-[11px] tracking-[0.16em] uppercase font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900">
                  Featured Pick
                </span>
                {product.categoryName ? (
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {product.categoryName}
                  </span>
                ) : null}
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[0.95] text-gray-900 dark:text-white mb-4 uppercase">
                {product.title}
              </h3>

              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed max-w-xl mb-6">
                Handpicked from our current catalog. Premium build quality, clean design, and reliable everyday performance.
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleBuyNow}
                  className="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-black hover:bg-black/85 dark:bg-white dark:hover:bg-gray-200 text-white dark:text-black font-semibold text-sm transition-colors"
                >
                  Buy now {formatPrice(finalPrice)}
                </button>
                <Link
                  href={productHref}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-gray-300 dark:border-[rgba(48,54,61,0.8)] text-gray-800 dark:text-gray-200 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <FiPlay className="w-4 h-4" />
                  View details
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-7 pt-5 border-t border-gray-200 dark:border-[rgba(48,54,61,0.6)]">
                <div className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Fast shipping</div>
                <div className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Secure checkout</div>
              </div>
            </div>

            <Link
              href={productHref}
              className="relative block overflow-hidden rounded-2xl border border-gray-200 dark:border-[rgba(48,54,61,0.7)] bg-gray-100 dark:bg-black/30 aspect-[16/10]"
            >
              <Image
                src={product.imageUrl || '/no-image.png'}
                alt={product.title}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
              <div className="absolute right-4 bottom-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 dark:bg-black/70 border border-gray-200 dark:border-[rgba(48,54,61,0.8)] px-3 py-1.5 text-xs font-semibold text-gray-800 dark:text-gray-200">
                View product <FiArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
